import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import type { AggregatedPnlItem } from '@/api/types';

interface AggregatedPnlChartDataItem extends AggregatedPnlItem {
    name: string;
    chartValue: number;
}

interface AggregatedPnlTooltipProps {
    active?: boolean;
    payload?: Array<{
        payload: AggregatedPnlChartDataItem;
    }>;
    currency: string;
}

/**
 * Custom tooltip for the aggregated P&L chart.
 * Displays total net profit, gain percentage, change from previous, and ledger count.
 */
export function AggregatedPnlTooltip({ active, payload, currency }: AggregatedPnlTooltipProps) {
    if (!active || !payload || payload.length === 0 || !payload[0]) {
        return null;
    }

    const data = payload[0].payload;
    const isPositive = data.totalNetProfit >= 0;

    // Determine change color
    const getChangeColor = (change: number | null) => {
        if (change === null) return '';
        return change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
    };

    const profitColorClass = isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 sm:p-3 text-sm sm:min-w-48">
            {/* Period label */}
            <div className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm mb-1 sm:mb-2">
                {data.periodLabel}
            </div>

            {/* Desktop: labeled horizontal layout */}
            <div className="hidden sm:block">
                {/* Total Net Profit */}
                <div className="flex items-center justify-between gap-4 mb-1">
                    <span className="text-gray-500 dark:text-gray-400">Net Profit:</span>
                    <span className={`font-semibold ${profitColorClass}`}>
                        {isPositive ? '+' : ''}
                        <MoneyDisplay
                            value={data.totalNetProfit}
                            currency={currency}
                            size="sm"
                            className="inline"
                        />
                    </span>
                </div>

                {/* Gain Percentage */}
                {data.aggregatedGainPercentage !== null && (
                    <div className="flex items-center justify-between gap-4 mb-1">
                        <span className="text-gray-500 dark:text-gray-400">Gain %:</span>
                        <span className={`font-medium ${data.aggregatedGainPercentage >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {data.aggregatedGainPercentage >= 0 ? '+' : ''}{data.aggregatedGainPercentage.toFixed(2)}%
                        </span>
                    </div>
                )}

                {/* Change from previous */}
                {data.changeFromPrevious !== null && (
                    <div className="flex items-center justify-between gap-4 mb-1">
                        <span className="text-gray-500 dark:text-gray-400">Change:</span>
                        <span className={getChangeColor(data.changeFromPrevious)}>
                            {data.changeFromPrevious >= 0 ? '+' : ''}
                            <MoneyDisplay
                                value={data.changeFromPrevious}
                                currency={currency}
                                size="sm"
                                className="inline"
                            />
                        </span>
                    </div>
                )}
            </div>

            {/* Mobile: vertical stacked layout with labels */}
            <div className="sm:hidden space-y-1">
                <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Net Profit</div>
                    <span className={`font-semibold ${profitColorClass}`}>
                        {isPositive ? '+' : ''}
                        <MoneyDisplay
                            value={data.totalNetProfit}
                            currency={currency}
                            size="sm"
                            className="inline"
                        />
                    </span>
                </div>
                {data.aggregatedGainPercentage !== null && (
                    <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Gain %</div>
                        <div className={`text-xs font-medium ${data.aggregatedGainPercentage >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {data.aggregatedGainPercentage >= 0 ? '+' : ''}{data.aggregatedGainPercentage.toFixed(2)}%
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
